// @ts-nocheck

import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import * as CommonIcons from '../../assets/svg/common';
import theme from '../../styled-components/dark';
import { BasketStatuses } from '../../types/basket';
import type { BasketStatus } from '../../types/basket';
import Link from '../Link';
import PendingLoader from '../PendingLoader';

const Icon = styled.div`
  position: relative;
  padding: ${0.8 / 3}em ${0.8}em;
  color: ${theme.InfosLabel.infosRed.backgroundColor};
  transform-style: preserve-3d;
  font-weight: bold;
  margin-right: 0.5em;

  &:after {
    content: '';
    position: absolute;
    width: ${0.8 * 2}em;
    height: ${0.8 * 2}em;
    left: 50%;
    top: 50%;
    transform: translate3d(-50%, -50%, -1px);
    border-radius: 50%;
    background-color: ${theme.InfosLabel.infosRed.symbolBackgroundColor};
  }
`;

export const StatusAsIcon = {
  [BasketStatuses.VALIDATED]: CommonIcons.Valid,
  [BasketStatuses.ERROR]: () => <Icon>!</Icon>,
  [BasketStatuses.REJECTED]: CommonIcons.Alert,
  [BasketStatuses.WAITING]: CommonIcons.Waiting,
  [BasketStatuses.PENDING]: CommonIcons.Waiting,
  [BasketStatuses.OK]: null,
};

const OPACITY_ANIMATION_DURATION = 150;

const Wrapper = styled.div`
  border-radius: 1.2em;
  padding: 0.4em 0.5em;
  cursor: pointer;

  display: flex;
  justify-content: space-between;
  align-items: center;

  transition: none ${OPACITY_ANIMATION_DURATION}ms ease-in-out;
  transition-property: background-color, color, opacity;

  ${({ $opacity }) => {
    return css`
      opacity: ${$opacity};
    `;
  }}

  ${({ $status }) => {
    const thm = theme.BasketAlert[$status];
    if (thm) {
      return css`
        color: ${thm.color};
        background-color: ${thm.backgroundColor};
      `;
    }
    return css`
      color: ${theme.BasketAlert.default.color};
      background-color: ${theme.BasketAlert.default.backgroundColor};
    `;
  }}
`;

const IconWrapper = styled.div`
  margin-left: 0.1em;
  margin-right: 0.5em;

  display: flex;
  align-items: center;

  svg {
    width: 1.5em;
    height: 1.5em;
  }
`;

const Left = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const BetshareIcon = styled(CommonIcons.Share)`
  width: 1.5em;
  height: 1.5em;
  float: right;
  cursor: pointer;
`;

const Message = styled.div`
  font-size: 1em;
`;

type Props = {
  status: BasketStatus;
  message: string;
  onClick?: (...params: unknown) => unknown;
  className?: string;
  betslipReference?: string | null;
};

const BasketAlert = ({ className, status, message, onClick = () => {}, betslipReference = null }: Props) => {
  const [opacity, setOpacity] = useState(0);
  const Icon = useMemo(() => {
    if (status === BasketStatuses.PENDING || status === BasketStatuses.WAITING) {
      // eslint-disable-next-line react/display-name
      return () => <PendingLoader duration={1000} />;
    } else {
      return StatusAsIcon[status];
    }
  }, [status]);

  useEffect(() => {
    setTimeout(() => {
      setOpacity(1);
    }, OPACITY_ANIMATION_DURATION);
  }, []);

  return (
    <Wrapper className={className} onClick={onClick} $status={status} $opacity={opacity}>
      <Left>
        {Icon ? (
          <IconWrapper>
            <Icon />
          </IconWrapper>
        ) : null}
        <Message>{message}</Message>
      </Left>
      {status === BasketStatuses.VALIDATED && betslipReference ? (
        <Link to={`#share=${betslipReference}`}>
          <BetshareIcon />
        </Link>
      ) : null}
    </Wrapper>
  );
};

export default BasketAlert;
